import { FEATURES } from '../../constants/features.constants';
import { isAccountAdmin, isCollaborator, isRoot } from './auth.selector';
import {
  isAvatarsEnabled,
  isKnowledgeHubAccessEnabled,
  isKnowledgeHubSharedEnabled,
  isOwnerPermissionsRestrictionEnabled,
} from './features.selector';
import { getUser } from './user.selector';
import { isAWorkspaceManager, userIsWorkspaceManager } from './workspace.selector';

/**
 * Check if the user have permissions and check feature restrictions
 * @param {ReduxState} { auth, organization }
 * @return {Boolean} if the user have permissions to edit the item
 */
export function hasPermissionsToEditItem({ auth, organization, workspace }) {
  // Check if the user is a viewer - no permissions to edit
  if (!isCollaborator({ auth })) {
    return false;
  }

  // Check if the user is a workspace manager - has permissions to edit
  if (userIsWorkspaceManager({ workspace, auth })) {
    return true;
  }

  const isAdmin = isAccountAdmin({ auth });
  const isFeatureEnabled = isOwnerPermissionsRestrictionEnabled(
    { auth, organization },
    FEATURES.PERMISSIONS_OWNER_RESTRICTION,
  );

  return isAdmin || !isFeatureEnabled;
}

/**
 * Check if the user have permissions to access knowledge hub
 * VIEWERs can access hub to view public organizations (read-only)
 * COLLABORATORs can access hub and import content
 * @param {ReduxState} { auth, organization }
 * @return {Boolean} if the user have permissions to see knowledge hub
 */
export function hasPermissionsToAccessKnowledgeHub({ auth, organization }) {
  // All users (including VIEWERs) can access the hub if feature is enabled
  const isFeatureEnabled = isKnowledgeHubAccessEnabled({ auth, organization });

  return isFeatureEnabled;
}

/**
 * Check if the user have permissions to share to knowledge hub
 * @param {ReduxState} { auth, organization }
 * @return {Boolean} if the user have permissions to share to knowledge hub
 */
export function hasPermissionsToShareToKnowledgeHub({ auth, organization }) {
  const hasPermission = isCollaborator({ auth });
  const isFeatureEnabled = isKnowledgeHubSharedEnabled({ auth, organization });

  return hasPermission && isFeatureEnabled;
}

/**
 * Check if user has permissions to see settings page
 * Account admins or collaborators that are workspace managers can see the settings page
 * @param {ReduxState} { auth, organization }
 * @return {Boolean} if the user have permissions to see settings page
 */
export function hasPermissionsToSeeSettings({ auth, workspace }) {
  const isAdmin = isAccountAdmin({ auth });
  const isManager = isAWorkspaceManager({ auth, workspace });

  return isAdmin || isManager;
}

/**
 * Check if user can import content from the knowledge hub
 * Only COLLABORATORs and above can import content (VIEWERs cannot)
 * @param {ReduxState} { auth, organization }
 * @return {Boolean} if the user can import from knowledge hub
 */
export function canImportFromKnowledgeHub({ auth, organization }) {
  const hasPermission = isCollaborator({ auth });
  const isFeatureEnabled = isKnowledgeHubAccessEnabled({ auth, organization });

  return hasPermission && isFeatureEnabled;
}

/**
 * Mobile-side UX gate for the "create educational event" action.
 * Backend enforces the real permission check.
 * @param {ReduxState} { auth }
 * @return {Boolean}
 */
export function canCreateEducationalEvent({ auth }) {
  return isCollaborator({ auth });
}

/**
 * UX gate for editing a user's avatar. Curried for use with `useSelector(canEditAvatar(user))`.
 *
 * No cross-org check: data layer scopes users to actor's current org; org-switch refreshes
 * both auth permissions and the user list, so stale cross-org targets disappear before this
 * is called. Backend remains the gatekeeper for URL-hack edge cases.
 *
 * @param {import('../../types/user').User | null | undefined} target - the user whose avatar would be edited
 * @return {(state: ReduxState) => boolean}
 */
export const canEditAvatar = target => state => {
  if (!target) return false;
  if (isRoot(state)) return true;
  const isSelf = getUser(state)?.id === target.id;
  const allowedRole = isSelf || isAccountAdmin(state);
  return allowedRole && isAvatarsEnabled(state);
};

/**
 * ROOT bypasses the avatars flag, mirroring the backend's withIAM bypass.
 * @param {ReduxState} state
 * @return {Boolean}
 */
export const canBulkUploadAvatars = state =>
  isRoot(state) || (isAccountAdmin(state) && isAvatarsEnabled(state));
